import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { fromJS, List, Map } from 'immutable';
import { useHistory } from 'react-router-dom';
import isElectron from 'is-electron';

import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useIsMounted from 'flow/hooks/useIsMounted';
import { useModal } from 'flow/contexts/ModalProvider';
import Button from 'flow/components/ui-components/Button';
import Icon from 'flow/components/ui-components/Icon';

import { createRoomSession } from 'flow/actions/chat-actions';
import { setSnackbar } from 'flow/actions/app-actions';
import { fetchLobbyData } from 'flow/actions/meeting-lobby-actions';
import { joinMeetingMuted, requestPin } from 'flow/actions/meeting-actions';
import { fetchSocialIntegrationSettings } from 'flow/actions/social-integration-actions';
import {
    getConferenceLink,
    getMeetingCode,
    hasMediaPermissions,
} from 'flow/utils/meeting-utils';
import { getUnlinkedMeetingCode } from 'flow/api-clients/conference-convert-client';
import {
    buildGoogleScheduleUrl,
    buildOutlookScheduleUrl,
    nextHalfHour,
} from 'flow/utils/outlook-schedule-utils';
import { MeetingType } from 'flow/utils/meeting-constants';
import { selectNameInApp } from 'flow/selectors/selectors';
import { selectUserCountryCode } from 'flow/selectors/ExtensionSelectors';
import { selectHasMicrosoftAccountLinked } from 'flow/selectors/social-integration-selectors';
import { selectLobbyNumber } from 'flow/selectors/meeting-selectors';
import { routes } from 'flow/types/routes';
import { generatePath } from 'flow/utils/router-utils';

const NewMeetingDropdown = () => {
    const [open, setOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { ownExtension } = useExtension();
    const { openModal } = useModal();
    const nameInApp = useAppSelector(selectNameInApp);
    const countryCode = useAppSelector(selectUserCountryCode);
    const hasMicrosoftAccountLinked = useAppSelector(
        selectHasMicrosoftAccountLinked,
    );

    const isMounted = useIsMounted();

    useEffect(() => {
        dispatch(fetchSocialIntegrationSettings());
    }, [dispatch]);

    const close = () => setOpen(false);

    const defaultRoomName = (date: Date) =>
        i18next.t('meetings.new_meeting.default_room_name', {
            date: moment(date).format('MMMM D HH:mm'),
        });

    // Wraps createRoomSession's callback-style API in a promise so its
    // onSuccess can be plain async code; a throw from onSuccess rejects.
    // Resolves with whatever onSuccess resolves to.
    const createRoom = <T,>(
        sessionDTO: unknown,
        onSuccess: (chatSessionKey: string) => Promise<T>,
    ) =>
        new Promise<T>((resolve, reject) => {
            dispatch(
                createRoomSession(
                    sessionDTO,
                    null,
                    async (chatSessionKey: string) => {
                        try {
                            resolve(await onSuccess(chatSessionKey));
                        } catch (e) {
                            reject(e);
                        }
                    },
                    reject,
                ),
            );
        });

    const handleInstantMeeting = async () => {
        if (isCreating) return;
        close();
        setIsCreating(true);

        const roomName = defaultRoomName(new Date());

        const sessionDTO = fromJS({
            name: roomName,
            roomType: 'room',
            members: List([Map({ key: ownExtension?.get('chatUserKey') })]),
        });

        try {
            await createRoom(sessionDTO, async (chatSessionKey) => {
                const meetingCode: string = await dispatch(
                    getMeetingCode(chatSessionKey, MeetingType.INSTANT.key),
                );
                if (!meetingCode) throw new Error('No meeting code');
                await dispatch(fetchLobbyData(chatSessionKey));

                const permissionsGranted = await hasMediaPermissions();
                if (!isMounted.current) return;

                if (permissionsGranted) {
                    if (nameInApp) {
                        await dispatch(
                            joinMeetingMuted(nameInApp, meetingCode),
                        );
                    }
                    history.push(
                        generatePath(routes['messages.id.lobby'], {
                            id: chatSessionKey,
                        }),
                        { from: routes['messages.meetings'] },
                    );
                } else {
                    history.push(
                        generatePath(routes['messages.id.lobby'], {
                            id: chatSessionKey,
                        }),
                        { from: routes['messages.meetings'] },
                    );
                }
            });
        } catch {
            if (!isMounted.current) return;
            dispatch(
                setSnackbar({
                    i18nextMessage: 'meetings.instant_meeting.error',
                }),
            );
        } finally {
            if (isMounted.current) setIsCreating(false);
        }
    };

    const handleCreateMeetingRoom = () => {
        close();
        openModal('createMeetingRoom');
    };

    type MeetingJoinInfo = {
        joinLink: string;
        number?: string;
        pin?: string;
    };

    // Google path only (no backend counterpart currently reconciles a Google
    // event to a room): creates a room-for-later up front and returns its
    // joining info.
    const createMeetingViaRoom = (
        roomName: string,
    ): Promise<MeetingJoinInfo> => {
        const sessionDTO = fromJS({
            name: roomName,
            roomType: 'room',
            members: List([Map({ key: ownExtension.get('chatUserKey') })]),
        });

        return createRoom(sessionDTO, async (chatSessionKey) => {
            const meetingCode: string = await dispatch(
                getMeetingCode(chatSessionKey, MeetingType.ROOM_FOR_LATER.key),
            );
            if (!meetingCode) throw new Error('No meeting code');

            const joinLink = getConferenceLink(meetingCode, countryCode);
            if (!joinLink) throw new Error('No join link');

            // requestPin must run after fetchLobbyData: both write
            // lobby.pin, and a freshly created room has no PIN until
            // requestPin generates one — so it must be the last writer
            // (see CreateMeetingRoomModal's handleRoomCreated).
            await dispatch(fetchLobbyData(chatSessionKey, true, meetingCode));
            const resultPin: string = await dispatch(requestPin(meetingCode));
            const pin = resultPin?.replace(/ /g, '');
            const number = dispatch((_dispatch, getState) =>
                selectLobbyNumber(getState()),
            );

            return { joinLink, number, pin };
        });
    };

    // Outlook path (AC-1714): mirrors the Outlook add-in's model instead of
    // eagerly creating a room. Only a bare, unlinked video-only meeting code
    // is requested from Home's external convert endpoint — the same one the
    // add-in uses — so no chat room exists until the backend calendar sync
    // later detects the saved Outlook event and reconciles a room onto it.
    const createMeetingViaLazyCode = async (): Promise<MeetingJoinInfo> => {
        const meetingCode = await getUnlinkedMeetingCode();
        if (!meetingCode) throw new Error('No meeting code');

        const joinLink = getConferenceLink(meetingCode, countryCode);
        if (!joinLink) throw new Error('No join link');

        // PIN and dial-in number don't require a room: requestPin generates
        // and persists a PIN directly on the conference, and the number is
        // the organizer's own account/country dial-in — mirrors
        // createMeetingViaRoom below, minus the room.
        // requestPin must run after fetchLobbyData: both write lobby.pin,
        // and a freshly created conference has no PIN until requestPin
        // generates one — so it must be the last writer (see
        // CreateMeetingRoomModal's handleRoomCreated).
        await dispatch(fetchLobbyData(null, true, meetingCode));
        const resultPin: string = await dispatch(requestPin(meetingCode));
        const pin = resultPin?.replace(/ /g, '');
        const number = dispatch((_dispatch, getState) =>
            selectLobbyNumber(getState()),
        );

        return { joinLink, number, pin };
    };

    const scheduleMeeting = async (
        createMeeting: (roomName: string) => Promise<MeetingJoinInfo>,
        buildUrl: (params: {
            roomName: string;
            joinLink: string;
            number?: string;
            pin?: string;
            i18next: typeof i18next;
            start: Date;
        }) => string,
        errorMessageKey: string,
    ) => {
        if (isCreating) return;
        close();
        if (!ownExtension) return;

        // Web: window.open() must run synchronously in the click's call
        // stack, or the popup blocker silently blocks it once we're past
        // the first `await` below; opened blank now, its location set once
        // the URL is ready. Electron doesn't need this: window-listeners.js
        // denies every window.open (always null) and hands the URL to the
        // OS browser directly, so it opens the finished URL once built.
        let scheduleWindow: Window | null = null;
        if (!isElectron()) {
            scheduleWindow = window.open('', '_blank');
            if (!scheduleWindow) {
                dispatch(setSnackbar({ i18nextMessage: errorMessageKey }));
                return;
            }
        }

        setIsCreating(true);

        const startTime = nextHalfHour();
        const roomName = defaultRoomName(startTime);

        try {
            const { joinLink, number, pin } = await createMeeting(roomName);

            const url = buildUrl({
                roomName,
                joinLink,
                number,
                pin,
                i18next,
                start: startTime,
            });

            if (isElectron()) {
                window.open(url, '_blank');
            } else {
                scheduleWindow.location.href = url;
            }
        } catch {
            scheduleWindow?.close();
            if (!isMounted.current) return;
            dispatch(setSnackbar({ i18nextMessage: errorMessageKey }));
        } finally {
            if (isMounted.current) setIsCreating(false);
        }
    };

    const handleScheduleInOutlook = () => {
        if (!hasMicrosoftAccountLinked) {
            close();
            return;
        }
        scheduleMeeting(
            createMeetingViaLazyCode,
            buildOutlookScheduleUrl,
            'meetings.schedule_in_outlook.error',
        );
    };

    const handleScheduleInGoogleCalendar = () =>
        scheduleMeeting(
            createMeetingViaRoom,
            buildGoogleScheduleUrl,
            'meetings.schedule_in_google_calendar.error',
        );

    const meetingOptions = [
        {
            icon: 'video' as const,
            label: i18next.t('meetings.new_meeting.instant_meeting'),
            onClick: handleInstantMeeting,
        },
        {
            icon: 'add' as const,
            label: i18next.t('meetings.new_meeting.create_room_for_later'),
            onClick: handleCreateMeetingRoom,
        },
        ...(ownExtension && hasMicrosoftAccountLinked
            ? [
                  {
                      icon: 'calendar' as const,
                      label: i18next.t(
                          'meetings.new_meeting.schedule_in_outlook',
                      ),
                      onClick: handleScheduleInOutlook,
                  },
              ]
            : []),
        ...(ownExtension
            ? [
                  {
                      icon: 'calendar' as const,
                      label: i18next.t(
                          'meetings.new_meeting.schedule_in_google_calendar',
                      ),
                      onClick: handleScheduleInGoogleCalendar,
                  },
              ]
            : []),
    ];

    return (
        <div className="relative flex-1">
            <Button
                color="primary"
                iconKey="videoCall"
                onClick={() => !isCreating && setOpen((v) => !v)}
                className="h-12 w-full min-w-0 rounded px-4"
                disabled={isCreating}
            >
                {isCreating
                    ? i18next.t('meetings.instant_meeting.creating')
                    : i18next.t('meetings.new_meeting')}
            </Button>
            {open && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        role="button"
                        tabIndex={-1}
                        aria-label="Close menu"
                        onClick={close}
                        onKeyDown={(e) => e.key === 'Escape' && close()}
                    />
                    <div className="absolute start-0 top-full z-50 mt-1 w-full rounded bg-white p-1 shadow-lg">
                        {meetingOptions.map(({ icon, label, onClick }) => (
                            <button
                                key={label}
                                onClick={onClick}
                                className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-start text-sm text-gray-800 hover:bg-gray-100"
                            >
                                <Icon
                                    iconKey={icon}
                                    className="text-gray-600"
                                />
                                {label}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default NewMeetingDropdown;
